import React from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { FormattedMessage } from 'react-intl';

// Components
import HousingAssistanceVoucherModals from './HousingAssistanceVoucherModals';
import {
  Table,
  TableHeader,
} from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import AddNewVoucherButton from './AddNewVoucherButton';

// Hooks
import useHousingAssistanceVoucher from './hooks';

// Others
import { HousingAssistanceVoucher } from './types';
import messages from './messages';

type Props = {
  organizationId: string,
  propertyId: string,
  householdId: string,
  householdMoveInDate: Date,
  stage: string,
  intl: Object,
};

/**
 * The central component for the Housing Assistance Voucher feature.
 *
 * @NOTE
 *  - All the housing assistance types will behave as vouchers, so that's why we added 'voucher' to the name
 * @param {*} param0
 * @returns
 */
function HousingAssistanceVoucherTab({
  organizationId,
  propertyId,
  householdId,
  stage,
  householdMoveInDate,
  transferDate,
  intl,
}: Props) {
  const {
    modalProps,
    // Table
    handleSortChange,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleDateSubmit,
    isLoading,
    allColumnsHidden,
    columnOptions,
    filteredHeaders,
    selectedColumns,
    handleColumnChange,
    count,
    totalCount,
    name,
    order,
    orderBy,
    // Data
    refetchVouchers,
    rows,
  } = useHousingAssistanceVoucher({
    organizationId,
    propertyId,
    householdId,
    stage,
  });

  const housingAssistanceVouchers: Array<HousingAssistanceVoucher> = rows;
  const anyVoucherIsFuture = housingAssistanceVouchers.some(
    (
      voucher, // check voucherStart date:
    ) => new Date(voucher.voucherStartDate?.value) > new Date(),
  );
  const disabledAddVoucher =
    isLoading ||
    anyVoucherIsFuture ||
    (stage === 'applicant' && !householdMoveInDate);

  const calculateDisableReason = () => {
    if (anyVoucherIsFuture) {
      return 'futureVoucher';
    } else if (!householdMoveInDate) {
      return 'noMoveInDate';
    } else {
      return null;
    }
  };

  // Determine the reason for disabling to show appropriate tooltip
  const disableReason = calculateDisableReason();

  return (
    <>
      <TableHeader
        title={<FormattedMessage {...messages.title} />}
        count={count}
        totalCount={totalCount}
        actions={
          <AddNewVoucherButton
            organizationId={organizationId}
            propertyId={propertyId}
            householdId={householdId}
            intl={intl}
            handleOpenModal={modalProps.handleOpen}
            disabled={disabledAddVoucher}
            disableReason={disableReason}
          />
        }
      />
      <Table
        ReactBeautifulDnD={{
          DragDropContext,
          Droppable,
          Draggable,
        }}
        allColumnsHidden={allColumnsHidden}
        columnOptions={columnOptions}
        count={count}
        totalCount={totalCount}
        handleColumnChange={handleColumnChange}
        h
        andleSortChange={handleSortChange}
        handleFilterChange={handleFilterChange}
        handleFilterTypeChange={handleFilterTypeChange}
        handleSearchSubmit={handleSearchSubmit}
        handleDateSubmit={handleDateSubmit}
        headers={filteredHeaders}
        height={500}
        intl={intl}
        isLoading={isLoading}
        name={name}
        order={order}
        orderBy={orderBy}
        rows={housingAssistanceVouchers}
        selectedColumns={selectedColumns}
        showSettings={false}
      />
      <HousingAssistanceVoucherModals
        organizationId={organizationId}
        propertyId={propertyId}
        householdId={householdId}
        refetchVouchers={refetchVouchers}
        stage={stage}
        householdMoveInDate={householdMoveInDate}
        transferDate={transferDate}
        intl={intl}
        {...modalProps}
      />
    </>
  );
}

export default HousingAssistanceVoucherTab;
